import { useState } from "react";
import { fmtUSD, toDollars } from ".";
import { CostOfTutoringBreakdown } from "client/openapi";
import { RevenueBreakdown } from "client/openapi";
import { Button } from "components/Button";

type Row = {
  key: string;
  name: string;
  revenue: number;
  cost: number;
  net: number;
};

type Metric = "revenue" | "cost" | "net";
type BreakdownType = "subject" | "tutor" | "student";

// Keys are "First_Last_id" for people and "Name_id" for subjects.
const cleanName = (raw: string, person: boolean) => {
  const name = raw.replace(/_\d+$/, "");
  return person ? name.replace(/_/g, " ") : name;
};

/** Breakdown list with absolute $ + percent */
export default function FinancialBreakdown({
  revenue,
  cost,
}: {
  revenue: RevenueBreakdown;
  cost: CostOfTutoringBreakdown;
}) {
  const breakdownData = {
    subject: buildRows(
      revenue.revenue_by_subject || {},
      cost.cost_by_subject || {},
      false
    ),
    tutor: buildRows(
      revenue.revenue_by_tutor || {},
      cost.cost_by_tutor || {},
      true
    ),
    student: buildRows(
      revenue.revenue_by_student || {},
      cost.cost_by_student || {},
      true
    ),
  } as const;

  const [breakdownType, setBreakdownType] = useState<BreakdownType>("subject");
  const [metric, setMetric] = useState<Metric>("revenue");

  const rows = breakdownData[breakdownType];
  const maxItems = 10;

  const displayRows = [...rows]
    .sort((a, b) => Math.abs(b[metric]) - Math.abs(a[metric]))
    .slice(0, maxItems);
  const total = displayRows.reduce((sum, r) => sum + Math.abs(r[metric]), 0);

  const barColor = (val: number) => {
    if (metric === "revenue") return "bg-emerald-500";
    if (metric === "cost") return "bg-amber-500";
    return val >= 0 ? "bg-blue-500" : "bg-rose-500";
  };

  return (
    <div className="space-y-4 bg-blue-50/50 p-6 rounded-lg border border-gray-200 shadow-sm">
      <div className="flex flex-wrap gap-4 items-center">
        {/* metric */}
        <div className="flex gap-2">
          {(["revenue", "cost", "net"] as const).map((m) => (
            <Button
              key={m}
              onClick={() => setMetric(m)}
              className={`px-4 py-1 rounded-full text-sm font-medium border transition-colors ${
                metric === m
                  ? m === "revenue"
                    ? "bg-emerald-600 text-white"
                    : m === "cost"
                    ? "bg-amber-600 text-white"
                    : "bg-blue-600 text-white"
                  : "bg-white border-gray-300 text-gray-700 hover:bg-gray-100"
              }`}
            >
              {m.replace(/^[a-z]/, (c) => c.toUpperCase())}
            </Button>
          ))}
        </div>
        {/* Breakdown type selector */}
        <div className="flex gap-2">
          {(["subject", "tutor", "student"] as const).map((type) => (
            <Button
              key={type}
              onClick={() => setBreakdownType(type)}
              className={`px-4 py-1 rounded-full text-sm font-medium border transition-colors ${
                breakdownType === type
                  ? "bg-purple-600 text-white"
                  : "bg-white border-gray-300 text-gray-700 hover:bg-gray-100"
              }`}
            >
              {type.replace(/^[a-z]/, (c) => c.toUpperCase())}
            </Button>
          ))}
        </div>
      </div>

      <div className="space-y-2">
        {!displayRows.length && (
          <p className="text-sm text-gray-500">
            No revenue or cost in these dates.
          </p>
        )}
        {displayRows.map((r) => {
          const val = r[metric];
          const pct = total ? (Math.abs(val) / total) * 100 : 0;
          return (
            <div key={r.key} className="space-y-1">
              <div className="flex justify-between text-sm">
                <span>{r.name}</span>
                <span
                  className={
                    metric === "net" && val < 0 ? "text-red-600" : "font-medium"
                  }
                >
                  {fmtUSD(val)}
                  <span className="text-gray-500">({pct.toFixed(1)}%)</span>
                </span>
              </div>
              <div className="h-2 w-full bg-gray-200 rounded">
                <div
                  className={`h-2 rounded ${barColor(val)}`}
                  style={{ width: `${pct}%` }}
                />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function buildRows(
  revenueMap: Record<string, number> | null,
  costMap: Record<string, number> | null,
  person: boolean
): Row[] {
  const keys = new Set([
    ...(revenueMap ? Object.keys(revenueMap) : []),
    ...(costMap ? Object.keys(costMap) : []),
  ]);
  return Array.from(keys).map((k) => {
    const revenue = toDollars(revenueMap?.[k]);
    const cost = toDollars(costMap?.[k]);
    return {
      key: k,
      name: cleanName(k, person),
      revenue,
      cost,
      net: revenue - cost,
    };
  });
}
